import * as React from 'react';
import { Avatar, Box, Button, Chip, CircularProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { VirtualMatchState, VirtualProviderMatchSummaryDto } from '../../models/dtos/VirtualProviderCatalogDto';

/**
 * Match state arrives as a numeric code from the main database and as a name from the provider,
 * so both spellings are accepted wherever a state is rendered.
 */
export type MatchStateValue = number | VirtualMatchState | null | undefined;

function stateName(state: MatchStateValue): string {
    if (typeof state === 'string') return state;
    switch (state) {
        case 1: return 'Future';
        case 2: return 'Live';
        case 3: return 'Finished';
        case 5: return 'Cancelled';
        default: return 'Processing';
    }
}

export function stateLabel(state: MatchStateValue): string {
    return stateName(state);
}

/** Label for the button that opens a virtual match's dedicated live/replay page, based on its state. */
export function liveActionLabel(state: MatchStateValue): string {
    switch (stateName(state)) {
        case 'Future': return 'Live countdown';
        case 'Finished': return 'Replay match';
        default: return 'Live match';
    }
}

function coefficient(value?: number | null): string {
    return value == null ? '—' : value.toFixed(2);
}

/** Kickoff time, always shown so a match can be placed on the schedule at a glance. */
export function formatKickoff(startDate?: string | null): string {
    if (!startDate) return '—';
    const parsed = new Date(startDate);
    return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString();
}

/** A team's logo next to its name; falls back to the initial when the team has no image yet. */
export function TeamBadge(props: { name: string; imageUrl?: string | null; size?: number }) {
    const size = props.size ?? 24;
    return (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Avatar
                src={props.imageUrl ?? undefined}
                alt={props.name}
                sx={{ width: size, height: size, fontSize: size * 0.5, bgcolor: 'action.selected' }}
            >
                {props.name.slice(0, 1).toUpperCase()}
            </Avatar>
            <Typography variant="body2" noWrap>{props.name}</Typography>
        </Stack>
    );
}

/** Home and away badges stacked, used anywhere a match is listed. */
export function MatchTeams(props: { match: VirtualProviderMatchSummaryDto; size?: number }) {
    return (
        <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <TeamBadge name={props.match.homeTeam.name} imageUrl={props.match.homeTeam.imageUrl} size={props.size} />
            <TeamBadge name={props.match.awayTeam.name} imageUrl={props.match.awayTeam.imageUrl} size={props.size} />
        </Stack>
    );
}

export default function VirtualMatchesTable(props: {
    matches: VirtualProviderMatchSummaryDto[];
    /** Receives the provider sync id; the caller resolves the BetUp id before navigating. */
    onOpenMatch: (syncId: string) => void;
    onEditMatch: (match: VirtualProviderMatchSummaryDto) => void;
    /** Sync id currently being resolved, so the row can show progress. */
    openingSyncId?: string | null;
}) {
    if (props.matches.length === 0) {
        return <Typography color="text.secondary">No matches found.</Typography>;
    }

    return (
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell>Match</TableCell>
                        <TableCell>Kick-off</TableCell>
                        <TableCell>League</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">1</TableCell>
                        <TableCell align="right">X</TableCell>
                        <TableCell align="right">2</TableCell>
                        <TableCell align="right">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {props.matches.map(match => {
                        const opening = props.openingSyncId === match.id;
                        return (
                            <TableRow
                                key={match.id}
                                hover
                                sx={{ cursor: 'pointer' }}
                                onClick={() => props.onOpenMatch(match.id)}
                            >
                                <TableCell><MatchTeams match={match} /></TableCell>
                                <TableCell>
                                    <Typography variant="body2">{formatKickoff(match.startDate)}</Typography>
                                </TableCell>
                                <TableCell>
                                    <TeamBadge name={match.league.name} imageUrl={match.league.imageUrl} size={20} />
                                </TableCell>
                                <TableCell>{match.scoreHome} : {match.scoreAway}</TableCell>
                                <TableCell><Chip size="small" label={stateLabel(match.state)} /></TableCell>
                                <TableCell align="right">{coefficient(match.mainOdds?.win1)}</TableCell>
                                <TableCell align="right">{coefficient(match.mainOdds?.draw)}</TableCell>
                                <TableCell align="right">{coefficient(match.mainOdds?.win2)}</TableCell>
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                        {opening ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                                        <Button size="small" disabled={opening} onClick={event => {
                                            event.stopPropagation();
                                            props.onOpenMatch(match.id);
                                        }}>Details</Button>
                                        <Button size="small" onClick={event => {
                                            event.stopPropagation();
                                            props.onEditMatch(match);
                                        }}>Edit</Button>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
